import { apiClient } from "@api/client";
import { request } from "@api/request";
import type { components } from "@api/schema";
import type { AudioGateMode } from "@rooms/roomsClient";

export interface CaptureSegment {
  kind: "flowing" | "gap";
  start: string;
  end: string;
}

export interface VisitPresence {
  visitUuid: string;
  patientName: string | null;
  enteredAt: string;
  /** Null while the patient is still in the room. */
  leftAt: string | null;
}

export interface RoomCaptureTimeline {
  roomUuid: string;
  roomName: string;
  segments: CaptureSegment[];
  /** Un-clamped: may extend outside the window; rendering clamps. */
  stays: VisitPresence[];
}

interface CaptureTimeline {
  windowStart: string;
  windowEnd: string;
  rooms: RoomCaptureTimeline[];
}

const toCaptureSegment = (
  s: components["schemas"]["TimelineSegmentOut"],
): CaptureSegment => ({
  kind: s.kind,
  start: s.start,
  end: s.end,
});

const toVisitPresence = (
  s: components["schemas"]["VisitPresenceOut"],
): VisitPresence => ({
  visitUuid: s.visit_uuid,
  patientName: s.patient_name,
  enteredAt: s.entered_at,
  leftAt: s.left_at,
});

const toRoomCaptureTimeline = (
  r: components["schemas"]["RoomCaptureTimelineOut"],
): RoomCaptureTimeline => ({
  roomUuid: r.room_uuid,
  roomName: r.room_name,
  segments: r.segments.map(toCaptureSegment),
  stays: r.stays.map(toVisitPresence),
});

const toCaptureTimeline = (
  t: components["schemas"]["CaptureTimelineOut"],
): CaptureTimeline => ({
  windowStart: t.window_start,
  windowEnd: t.window_end,
  rooms: t.rooms.map(toRoomCaptureTimeline),
});

/** `connectorLive` false means the row may be stale: unknown, not offline. */
export interface CaptureNode {
  uuid: string;
  instanceId: string;
  roomUuid: string | null;
  roomName: string;
  online: boolean;
  recording: boolean;
  lastSeen: string;
  reportedAt: string;
  connectorUuid: string;
  connectorLive: boolean;
  captureEnabled: boolean | null;
  captureRevision: number;
  controlConnected: boolean;
  controlError: string | null;
  desiredEnabled: boolean | null;
  desiredRevision: number;
  /**
   * State/level/threshold/open are null while not streaming; all gate fields are
   * null (revision 0) from a Zuul predating the gate. `gateLevel` is the peak
   * filtered RMS second since Zuul's previous snapshot; `gateMarginDb` is null
   * outside adaptive mode. Zuul re-sends on a gate flip or 6 dB level step, at
   * least every 5 minutes.
   */
  gateMode: string | null;
  gateState: string | null;
  gateLevel: number | null;
  gateFloor: number | null;
  gateThreshold: number | null;
  gateMarginDb: number | null;
  gateOpen: boolean | null;
  gateRevision: number;
  desiredGateMode: AudioGateMode;
  desiredGateMarginDb: number | null;
  desiredGateRevision: number;
}

const toCaptureNode = (
  n: components["schemas"]["CaptureNodeOut"],
): CaptureNode => ({
  uuid: n.uuid,
  instanceId: n.instance_id,
  roomUuid: n.room_uuid,
  roomName: n.room_name,
  online: n.online,
  recording: n.recording,
  lastSeen: n.last_seen,
  reportedAt: n.reported_at,
  connectorUuid: n.connector_uuid,
  connectorLive: n.connector_live,
  captureEnabled: n.capture_enabled,
  captureRevision: n.capture_revision,
  controlConnected: n.control_connected,
  controlError: n.control_error,
  desiredEnabled: n.desired_enabled,
  desiredRevision: n.desired_revision,
  gateMode: n.gate_mode,
  gateState: n.gate_state,
  gateLevel: n.gate_level,
  gateFloor: n.gate_floor,
  gateThreshold: n.gate_threshold,
  gateMarginDb: n.gate_margin_db,
  gateOpen: n.gate_open,
  gateRevision: n.gate_revision,
  desiredGateMode: n.desired_gate_mode,
  desiredGateMarginDb: n.desired_gate_margin_db,
  desiredGateRevision: n.desired_gate_revision,
});

export interface RoomCaptureCommand {
  roomUuid: string;
  enabled: boolean;
  revision: number;
  updatedAt: string;
}

export interface DeviceEnrollment {
  code: string;
  expiresAt: number;
}

/** The whole desired gate; the PUT replaces both knobs every time. */
export interface RoomAudioGateInput {
  mode: AudioGateMode;
  /** dB above the learned floor; null = Zuul's env default (12). */
  marginDb: number | null;
}

export interface RoomAudioGateCommand extends RoomAudioGateInput {
  roomUuid: string;
  revision: number;
  updatedAt: string;
}

export const captureClient = {
  /** `date` defaults server-side to the clinic-local current day. */
  timeline: (date?: string): Promise<CaptureTimeline> =>
    request(() =>
      apiClient.GET("/rooms/capture-timeline", {
        params: { query: date ? { date } : {} },
      }),
    ).then(toCaptureTimeline),

  nodes: (): Promise<CaptureNode[]> =>
    request(() => apiClient.GET("/capture-nodes")).then((rows) =>
      rows.map(toCaptureNode),
    ),

  /** Resolves once Brian has committed the command, not once the device applies it. */
  setRoomCapture: (
    roomUuid: string,
    enabled: boolean,
  ): Promise<RoomCaptureCommand> =>
    request(() =>
      apiClient.PUT("/rooms/{room_uuid}/capture", {
        params: { path: { room_uuid: roomUuid } },
        body: { enabled },
      }),
    ).then((result) => ({
      roomUuid: result.room_uuid,
      enabled: result.enabled,
      revision: result.revision,
      updatedAt: result.updated_at,
    })),

  enrollDevice: (roomUuid: string): Promise<DeviceEnrollment> =>
    request(() =>
      apiClient.POST("/rooms/{room_uuid}/device-enrollment", {
        params: { path: { room_uuid: roomUuid } },
      }),
    ).then((result) => ({
      code: result.code,
      expiresAt: Date.now() + result.ttl_seconds * 1000,
    })),

  /** `mode: null` hands the room back to Zuul's own default gate mode. */
  setRoomAudioGate: (
    roomUuid: string,
    gate: RoomAudioGateInput,
  ): Promise<RoomAudioGateCommand> =>
    request(() =>
      apiClient.PUT("/rooms/{room_uuid}/audio-gate", {
        params: { path: { room_uuid: roomUuid } },
        body: { mode: gate.mode, margin_db: gate.marginDb },
      }),
    ).then((result) => ({
      roomUuid: result.room_uuid,
      mode: result.mode,
      marginDb: result.margin_db,
      revision: result.revision,
      updatedAt: result.updated_at,
    })),
};
